import React, { useState, useEffect, useRef } from 'react';
import '../styles/Integrations.css';
import '../styles/BuyBoxForm.css';
import Button from './Button';
import Switch from './Switch';
import { setTransaction } from '../functions';

// Deal-alert notifications. Config lives on the company doc at data.notifications
// (beside data.buyBox / data.ingestionFilter) and is read by the V2 underwriting
// processor. Two independent emails:
//
//  • enabled       — the flip "new deal" email. Every fully-analyzed deal notifies
//                    the enabled recipients; the old per-company profit threshold
//                    was retired (deal "fit" lives in the strategyFit badges).
//  • coLivingAlert — a co-living email that fires ONLY when a deal clears the
//                    company's money bars. Off by default. See
//                    functions/propertyIngestionV2/underwriting/coLivingAlert.js.
//
// Both share the recipients list. NOTE: setTransaction replaces data.notifications
// wholesale, and the MCP update_notifications handler does the same, so every field
// must be round-tripped here or an MCP-set threshold is wiped on the next UI save.
function NotificationsSection({ companyId, initialData, users, onSaved, primaryButtonText, onSkip }) {
    const [enabled, setEnabled] = useState(false);
    const [emailByUser, setEmailByUser] = useState({});
    const [clEnabled, setClEnabled] = useState(false);
    // Kept as strings so the inputs can be cleared to empty ("" = bar not set).
    const [clCashFlow, setClCashFlow] = useState('');
    const [clCashOnCash, setClCashOnCash] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [savedFlash, setSavedFlash] = useState(false);

    // `initialData` comes from a LIVE onSnapshot on the company doc (CompanyContext),
    // so it gets a fresh identity on every write to that doc — a credit decrement
    // during ingestion, an MCP write, a Stripe webhook. Re-hydrating on each of those
    // would wipe whatever the user is part-way through typing. Hydrate once per
    // company instead.
    const hydratedFor = useRef(null);
    useEffect(() => {
        // Hydrate once per company. Re-running on every `initialData` identity change
        // would wipe in-progress typing, and a company switch still re-hydrates
        // because the ref no longer matches — so this single check covers both
        // without a separate dirty flag.
        if (hydratedFor.current === companyId) return;
        // Wait for the doc to actually arrive before claiming this company as
        // hydrated. initialData is undefined on the first render while the snapshot
        // loads; marking it hydrated then would make the real config — arriving one
        // render later — hit the guard above and never populate the form. A company
        // with genuinely no notifications config stays unhydrated, which is correct:
        // the initial state already IS the empty/default form.
        if (!initialData) return;
        hydratedFor.current = companyId;
        const cfg = initialData;
        setEnabled(cfg.enabled === true);
        const map = {};
        (cfg.recipients || []).forEach((r) => {
            if (r && r.userId) map[r.userId] = r.emailEnabled === true;
        });
        setEmailByUser(map);
        const cl = cfg.coLivingAlert || {};
        setClEnabled(cl.enabled === true);
        setClCashFlow(typeof cl.minCashFlowMonthly === 'number' ? String(cl.minCashFlowMonthly) : '');
        setClCashOnCash(typeof cl.minCashOnCash === 'number' ? String(cl.minCashOnCash) : '');
    }, [initialData, companyId]);

    // Must match BOUNDS in functions/propertyIngestionV2/underwriting/coLivingAlert.js
    // (the single backend source — the MCP handler imports it from there). Duplicated
    // here only because src/ cannot import from functions/.
    const BAR_BOUNDS = {
        cashFlow: { min: 0, max: 100000, label: 'Monthly cash flow' },
        cashOnCash: { min: 0, max: 100, label: 'Cash-on-cash' },
    };

    // "" / whitespace / non-numeric ⇒ null, meaning the bar is not set.
    // NOTE: the min/max attributes on the inputs are NOT enforcement — the inputs
    // are not in a <form> and nothing calls checkValidity(), and setTransaction is
    // a raw client write with no server validation. A negative cash-flow bar would
    // be cleared by every co-living deal, turning the alert into a firehose, so the
    // range is checked here explicitly (and again server-side, which is the real
    // guard — this is just so the user gets told why).
    const parseBar = (raw) => {
        const t = (raw || '').trim();
        if (t === '') return null;
        const n = Number(t);
        return Number.isFinite(n) ? n : null;
    };
    const clCashFlowNum = parseBar(clCashFlow);
    const clCashOnCashNum = parseBar(clCashOnCash);

    const barError = (() => {
        if (!clEnabled) return '';
        const checks = [
            [clCashFlow, clCashFlowNum, BAR_BOUNDS.cashFlow],
            [clCashOnCash, clCashOnCashNum, BAR_BOUNDS.cashOnCash],
        ];
        for (const [raw, num, b] of checks) {
            if ((raw || '').trim() === '') continue;
            if (num === null) return `${b.label} must be a number.`;
            if (num < b.min || num > b.max) return `${b.label} must be between ${b.min} and ${b.max.toLocaleString()}.`;
        }
        return '';
    })();

    // Mirrors the backend guard: alerting on with no bar set would email on every
    // co-living deal, so both the MCP handler and the ingestion gate refuse it.
    const clNoBars = clEnabled && clCashFlowNum === null && clCashOnCashNum === null;
    const clInvalid = clEnabled && (!!barError || clNoBars);
    // Recipients are shared, so the list stays editable if EITHER email is on.
    const anyAlertOn = enabled || clEnabled;

    const toggleUser = (userId) => {
        setEmailByUser((prev) => ({ ...prev, [userId]: !prev[userId] }));
    };

    const recipientCount = (users || []).filter((u) => emailByUser[u.userId]).length;

    const handleSave = async () => {
        if (!companyId) return;
        if (clInvalid) {
            setError(barError || 'Set a minimum cash flow or cash-on-cash before turning on co-living deal alerts.');
            return;
        }
        setError('');
        setSaving(true);
        try {
            const recipients = (users || []).map((u) => ({
                userId: u.userId,
                email: u.email || '',
                firstName: u.firstName || '',
                emailEnabled: !!emailByUser[u.userId]
            }));
            const payload = {
                enabled,
                recipients,
                coLivingAlert: {
                    enabled: clEnabled,
                    minCashFlowMonthly: clCashFlowNum,
                    minCashOnCash: clCashOnCashNum
                }
            };
            const res = await setTransaction('companies', companyId, 'notifications', payload, null);
            if (res?.status !== 200) throw new Error(res?.message || 'Could not save your notification settings.');
            setSavedFlash(true);
            setTimeout(() => setSavedFlash(false), 2000);
            if (onSaved) await onSaved();
        } catch (e) {
            setError(e.message || 'Could not save your notification settings.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="integrations-notifications">
            <div className="integrations-notif-master">
                <Switch
                    val={enabled}
                    setVal={setEnabled}
                    label="Email me about qualifying leads"
                    disabled={saving}
                />
            </div>

            <div className={`integrations-notif-body${enabled ? '' : ' integrations-notif-body-disabled'}`}>
            <div className="integrations-notif-body-inner">
                {enabled ? (
                    <div className="integrations-notif-readback body-regular colour-secondary">
                        <span><strong className="colour-primary">Every</strong> fully-analyzed deal will trigger an alert.</span>
                    </div>
                ) : null}
            </div>
            </div>

            {/* Co-living deal alerts — a separate email with its own criteria, sent to
                the same recipients. Deliberately outside the master switch above: a
                company can run co-living alerts without the every-deal email. */}
            <div className="integrations-notif-master">
                <Switch
                    val={clEnabled}
                    setVal={setClEnabled}
                    label="Email me about co-living deals that pass and hit my numbers"
                    disabled={saving}
                />
            </div>

            <div className={`integrations-notif-body${clEnabled ? '' : ' integrations-notif-body-disabled'}`}>
            <div className="integrations-notif-body-inner">
                <div className="integrations-notif-field">
                    <span className="body-semibold colour-primary">Minimum returns</span>
                    <span className="label-regular colour-secondary">
                        You'll be emailed about a co-living deal only if it already passes your
                        co-living criteria (bedrooms, parking and the rest) <em>and</em> clears the
                        numbers below. Leave one blank to ignore it.
                    </span>
                    <div className="integrations-notif-threshold-row">
                        <div className="integrations-notif-threshold-field">
                            <label className="label-regular colour-secondary" htmlFor="cl-alert-cash-flow">Monthly cash flow ($)</label>
                            <input
                                id="cl-alert-cash-flow"
                                className="buybox-form-input"
                                type="number"
                                min="0"
                                max="100000"
                                placeholder="e.g. 800"
                                value={clCashFlow}
                                onChange={(e) => setClCashFlow(e.target.value)}
                                disabled={saving || !clEnabled}
                            />
                        </div>
                        <div className="integrations-notif-threshold-and">
                            <span className="label-regular colour-secondary">and</span>
                        </div>
                        <div className="integrations-notif-threshold-field">
                            <label className="label-regular colour-secondary" htmlFor="cl-alert-coc">Cash-on-cash (%)</label>
                            <input
                                id="cl-alert-coc"
                                className="buybox-form-input"
                                type="number"
                                min="0"
                                max="100"
                                placeholder="e.g. 10"
                                value={clCashOnCash}
                                onChange={(e) => setClCashOnCash(e.target.value)}
                                disabled={saving || !clEnabled}
                            />
                        </div>
                    </div>
                    {clEnabled && !clInvalid ? (
                        <div className="integrations-notif-readback body-regular colour-secondary">
                            <span>
                                {'You\'ll get an email when a passing co-living deal makes '}
                                {clCashFlowNum !== null ? (
                                    <strong className="colour-primary">{`$${clCashFlowNum.toLocaleString()}/mo or more`}</strong>
                                ) : null}
                                {clCashFlowNum !== null && clCashOnCashNum !== null ? ' and returns ' : null}
                                {clCashOnCashNum !== null ? (
                                    <strong className="colour-primary">{`${clCashOnCashNum}% cash-on-cash or more`}</strong>
                                ) : null}
                                .
                            </span>
                        </div>
                    ) : null}
                    {barError ? (
                        <span className="label-regular colour-error">{barError}</span>
                    ) : clNoBars ? (
                        <span className="label-regular colour-error">
                            Set at least one number — otherwise every co-living deal would email you.
                        </span>
                    ) : null}
                </div>
            </div>
            </div>

            {/* Recipients are SHARED by both emails above, so this section stays live
                whenever either is on — not nested under the master switch. */}
            <div className={`integrations-notif-body${anyAlertOn ? '' : ' integrations-notif-body-disabled'}`}>
            <div className="integrations-notif-body-inner">
                <div className="integrations-notif-field">
                    <span className="body-semibold colour-primary">Recipients</span>
                    <span className="label-regular colour-secondary">Choose who on your team gets these emails.</span>
                    <div className="integrations-notif-recipients">
                        {(users || []).length === 0 ? (
                            <span className="body-regular colour-secondary">No teammates yet.</span>
                        ) : (
                            (users || []).map((u) => (
                                <div key={u.userId} className="integrations-notif-recipient-row">
                                    <div className="integrations-notif-recipient-id">
                                        <span className="body-semibold colour-primary">
                                            {`${u.firstName || ''} ${u.lastName || ''}`.trim() || 'Unknown'}
                                        </span>
                                        {u.email ? <span className="label-regular colour-secondary">{u.email}</span> : null}
                                    </div>
                                    <Switch
                                        val={!!emailByUser[u.userId]}
                                        setVal={() => toggleUser(u.userId)}
                                        label={null}
                                        disabled={saving || !anyAlertOn || !u.email}
                                    />
                                </div>
                            ))
                        )}
                    </div>
                    {anyAlertOn && recipientCount === 0 ? (
                        <span className="label-regular colour-secondary">No recipients selected — no emails will be sent.</span>
                    ) : null}
                </div>
            </div>
            </div>

            {error ? <span className="body-regular colour-error">{error}</span> : null}

            <div className={`buybox-form-save-row${onSkip ? ' buybox-form-save-row--onboarding' : ''}`}>
                <Button
                    buttonType="primary-button"
                    size="large"
                    text={savedFlash ? 'Saved' : (primaryButtonText || 'Save notifications')}
                    cta={handleSave}
                    disabled={saving}
                    spinner={saving}
                    leftIcon={null}
                    leftIconClass=""
                    rightIcon={null}
                    rightIconClass=""
                    alt=""
                    standardIcon={false}
                    badge={false}
                    badgeVal={null}
                    badgeBG={null}
                    buttonRef={null}
                    goldAccent={false}
                />
                {onSkip ? (
                    <span className="label-regular colour-secondary cursor-pointer text-align-center" onClick={onSkip}>
                        Skip, I'll set this up later
                    </span>
                ) : null}
            </div>
        </div>
    );
}

export default NotificationsSection;
